import { ReactNode, useEffect, useRef } from 'react';

import { Box } from '@mui/material';

import { ChartProvider } from '@/components/Chart/context/ChartContext';
import { CsvDownloadProvider } from '@/components/Chart/context/CsvTableContext';
import { Tag } from '@/components/Tag';
import ElectricBorder from '@/features/Dashboard/components/ElectricBorder/ElectricBorder';
import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import TabsContainer from '@/pages/Markets/MarketsComponents/components/TabsContainer';
import { MarketAppControlsProvider } from '@/pages/Markets/MarketsComponents/contexts/MarketAppControlsContext';
import { MarketWidgetProvider } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import { MarketsComponentType } from '@/slices/marketsSlice';

import Tiles from './Tiles';

type ComponentProps = {
  components: MarketsComponentType[] | undefined;
  last?: ReactNode;
  showShareBlock?: boolean;
  editMode?: boolean;
  showAddToDashboard?: boolean;
  forChat?: boolean;
};
export const ComponentMapper = ({
  component,
  blockIndex
}: {
  component: MarketsComponentType;
  blockIndex: number;
}) => {
  const { elementsToAnimateId, setElementsToAnimateId, animationTime } = useWidgetAnimation();

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    if (elementsToAnimateId?.includes(component.id)) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      timeoutId = setTimeout(() => {
        setElementsToAnimateId([]);
      }, animationTime);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [animationTime, component.id, elementsToAnimateId, setElementsToAnimateId]);

  if (component.component) {
    if (component.component.type === 'tiles') {
      return component.component.tiles?.length ? (
        <Tiles
          key={component.id}
          tiles={component.component.tiles}
        />
      ) : null;
    }
  } else if (component.tabs?.length) {
    return (
      <Box
        ref={ref}
        id={component.id}
        sx={{
          '&  .MuiPaper-root': {
            height: '100%'
          },
          '& > *': {
            height: '100%'
          }
        }}
      >
        <ElectricBorder animate={!!elementsToAnimateId?.includes(component.id)}>
          <MarketWidgetProvider>
            <ChartProvider>
              <CsvDownloadProvider>
                <TabsContainer
                  key={component.id}
                  blockIndex={blockIndex}
                  layout={component.layout}
                  tabs={component.tabs.map(tab => {
                    if (tab.type === 'feed') return { ...tab, tag: <Tag label='beta' /> };
                    return tab;
                  })}
                />
              </CsvDownloadProvider>
            </ChartProvider>
          </MarketWidgetProvider>
        </ElectricBorder>
      </Box>
    );
  }
};

/**
 * MarketsComponents - renders a collection of different market-related components
 */
const MarketsComponents = ({
  components,
  last,
  showShareBlock,
  editMode,
  showAddToDashboard = true,
  forChat = false
}: ComponentProps) => {
  return (
    <MarketAppControlsProvider
      editMode={editMode}
      showAddToDashboard={showAddToDashboard}
      showShareBlock={showShareBlock}
    >
      <Box
        className='apps__container'
        sx={{
          width: '100%',
          display: 'grid',
          gap: 3,
          gridTemplateColumns: forChat
            ? '1fr'
            : {
                xs: '1fr',
                lg: `repeat(2, 1fr)`
              },
          '& > *': {
            minWidth: 0,
            overflow: 'hidden'
          }
        }}
      >
        {components?.map((component, index) => {
          return (
            <ComponentMapper
              key={component.id}
              blockIndex={index}
              component={component}
            />
          );
        })}
        {last}
      </Box>
    </MarketAppControlsProvider>
  );
};

export default MarketsComponents;
